import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiCheck } from 'react-icons/fi';

/**
 * PricingTabsSection - Composant pour afficher les tarifs avec onglets
 * Utilisé notamment pour le Vidéobooth 360 (80cm / 100cm)
 */
const PricingTabsSection = ({ content, primaryColor, onBookingClick }) => {
  const [activeTab, setActiveTab] = useState(content?.tabs?.[0]?.id || '80cm');
  
  const activeTabData = content?.tabs?.find(t => t.id === activeTab) || content?.tabs?.[0];

  // Couleurs distinctives pour chaque formule
  const planColors = [
    { bg: 'from-slate-800/80 to-slate-900/80', border: '#64748b', accent: '#94a3b8' },
    { bg: 'from-blue-900/50 to-slate-900/80', border: '#3b82f6', accent: '#60a5fa' },
    { bg: 'from-purple-900/50 to-slate-900/80', border: '#8b5cf6', accent: '#a78bfa' },
    { bg: 'from-amber-900/50 to-slate-900/80', border: primaryColor, accent: primaryColor }
  ];

  if (!content?.tabs?.length) {
    return null;
  }

  return (
    <section className="py-24 px-6 bg-gradient-to-b from-black to-gray-900" id="tarifs" data-testid="pricing-tabs-section">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">{content.title}</h2>
        {content.subtitle && (
          <p className="text-white/60 text-center mb-8 text-lg">{content.subtitle}</p>
        )}
        
        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-8" data-testid="pricing-tabs">
          {content.tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`px-6 py-3 rounded-full font-bold transition-all ${activeTab === tab.id ? 'text-black' : 'text-white bg-white/10 hover:bg-white/20'}`}
              style={{ backgroundColor: activeTab === tab.id ? primaryColor : undefined }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {activeTabData?.description && (
          <p className="text-white/50 text-center mb-8">{activeTabData.description}</p>
        )}
        
        {/* Plans grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pricing-plans-grid">
          {activeTabData?.plans?.map((plan, i) => {
            const colors = planColors[i % planColors.length];
            const isHighlighted = plan.highlighted || i === activeTabData.plans.length - 1;
            
            return (
              <motion.div 
                key={`${activeTab}-${i}`}
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: i * 0.1 }}
                data-testid={`pricing-plan-${plan.name?.toLowerCase().replace(/\s+/g, '-') || i}`}
                className={`relative rounded-3xl p-6 bg-gradient-to-b ${colors.bg} border-2 hover:scale-[1.02] transition-transform`}
                style={{ borderColor: isHighlighted ? primaryColor : colors.border }}
              >
                {isHighlighted && (
                  <div 
                    className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-sm font-bold text-black" 
                    style={{ backgroundColor: primaryColor }}
                  >
                    ⭐ POPULAIRE
                  </div>
                )}
                <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                {plan.description && (
                  <p className="text-white/50 text-sm mb-3">{plan.description}</p>
                )}
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-4xl font-black" style={{ color: isHighlighted ? primaryColor : colors.accent }}>{plan.price}</span>
                  <span className="text-lg text-white/60">{plan.period || '€'}</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {plan.features?.map((feat, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm text-white/80">
                      <FiCheck className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: isHighlighted ? primaryColor : colors.accent }} />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={onBookingClick}
                  data-testid={`book-plan-${plan.name?.toLowerCase().replace(/\s+/g, '-') || i}`}
                  className={`w-full py-3 rounded-xl font-bold transition-all hover:brightness-110 text-sm ${isHighlighted ? 'text-black' : 'text-white'}`}
                  style={{ backgroundColor: isHighlighted ? primaryColor : colors.accent }}
                >
                  {plan.buttonText || 'Réserver'}
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PricingTabsSection;
